import React from 'react'
import {
  ISlideCardProps,
  ENUMSliderCardShadows,
  IRouteLayoutProps,
} from './types'
import NextImage from 'next/image'
import Anchor from '@/components/base/anchor'
import { functions, path, useApp } from '@wap-client/core'
import SocialShare from '@/components/widgets/social-share'
import Image from '@/components/base/image'
import { GTMEvent } from '@/utils/gtm'
import { Akiva } from '@/utils/akiva/akiva'
import { AkivaType } from '@/utils/akiva/types'
import useTranslations from '@/hooks/useTranslations'

const RouteLayout: React.FC<IRouteLayoutProps> = ({
  children,
  href,
  title,
  page,
  eventName,
  moreEventName,
  ...rest
}) => {
  const app = useApp()
  const onInteract = (page: String) => {
    let event = {}

    event = {
      page: page,
      title: title,
    }

    GTMEvent(moreEventName || eventName, event)
    Akiva({
      app,
      type: AkivaType.SubSite,
      data: { fullPath: page },
    })
  }

  if (href) {
    return (
      <Anchor href={href} onClick={() => onInteract(href)}>
        {children}
      </Anchor>
    )
  } else return <>{children}</>
}

const SlideCard: React.ForwardRefRenderFunction<
  HTMLDivElement,
  ISlideCardProps
> = ({
  title,
  desc,
  image,
  linkSrc,
  target,
  sizes,
  shadow,
  eventName,
  moreEventName,
  brokenImage,
  isSocialShader = false,
  isSustainable = false,
  isHorizontal = false,
}) => {
  const app = useApp()
  const { translation } = useTranslations()

  const renderSustainable = () => {
    const handleNotClick = (e: React.SyntheticEvent) => {
      e.preventDefault()
    }

    if (isSustainable) {
      return (
        <div
          className="slide-sustainable"
          onClick={handleNotClick}
          onKeyDown={handleNotClick}
          tabIndex={0}
          role="button"
        >
          <div className="slide-sustainable-layout">
            <NextImage
              className="slide-sustainable-layout-icon"
              src="/icons/icon-sustainability.svg"
              alt="Sustainability Icon"
              width={32}
              height={32}
            />
            <div className="slide-sustainable-layout-content">
              <span className="slide-sustainable-layout-content-title">
                {translation('sustainability-title')}
              </span>
              <p className="slide-sustainable-layout-content-desc">
                {translation('sustainability-desc')}
              </p>
            </div>
          </div>
        </div>
      )
    }
  }

  let link = ''

  if (linkSrc) {
    if (typeof linkSrc === 'object') {
      link = linkSrc.href || ''
    } else {
      link = linkSrc
    }

    if (!link.startsWith('http://') && !link.startsWith('https://')) {
      if (typeof linkSrc !== 'object') {
        link = 'https://' + link
      }
    }
  }

  return (
    <RouteLayout
      href={link}
      target={target}
      page={link}
      title={title}
      eventName={eventName}
      moreEventName={moreEventName}
    >
      <div
        className={functions.classnames(
          'slide-card',
          shadow && ENUMSliderCardShadows[shadow],
        )}
      >
        {!image ? (
          <img
            referrerPolicy="no-referrer"
            src={
              brokenImage?.src
                ? path.asset(app.environment, brokenImage.src)
                : '/images/hotel-broken-400-500.png'
            }
            alt={title ?? 'Broken image'}
            className="slide-card-image broken-image"
          />
        ) : typeof image === 'string' ? (
          <img
            referrerPolicy="no-referrer"
            src={image}
            alt={title ?? 'Card Image'}
            className="slide-card-image"
          />
        ) : (
          <Image
            {...image}
            alt={title ?? 'Card Image'}
            className="slide-card-image"
            referrerPolicy="no-referrer"
            onError={(e) => {
              e.currentTarget.onerror = null
              e.currentTarget.src = brokenImage?.src
                ? path.asset(app.environment, brokenImage.src)
                : '/images/hotel-broken-400-500.png'
              e.currentTarget.classList.add('image-broken')
            }}
          />
        )}

        <div
          className={functions.classnames(
            'slide-card-body',
            isHorizontal && 'horizontal',
          )}
        >
          {renderSustainable()}

          <div className="slide-card-body-content">
            {title && (
              <span className="slide-card-body-content-title">{title}</span>
            )}
            {isSocialShader && (
              <SocialShare title={title} desc={desc} url={link} />
            )}
            {desc && <p className="slide-card-body-content-desc">{desc}</p>}
          </div>
        </div>
      </div>
    </RouteLayout>
  )
}

export default SlideCard
